import { Fragment } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, BellIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { cciLogo } from "../../helpers/images";
import { classNames } from "../../helpers";
import { useAuth } from "../Auth/AuthProvider";

const navigation = [
  { name: "Dashboard", path: "/dashboard" },
  { name: "Events", path: "/dashboard/events" },
  // todo re-enable this when we show user's data on the page
  // { name: "Users", path: "/dashboard/users" },
];

const userNavigation = [
  { name: "My Profile", path: "/dashboard/profile" },
  { name: "Settings", path: "/dashboard/settings" },
];

const Dashboard = () => {
  const { pathname } = useLocation();
  const auth = useAuth();
  const user = auth.user;
  const navigate = useNavigate();

  function isActive(path) {
    return pathname === path;
  }

  function logout() {
    auth.logout(() => navigate("/"));
  }

  return (
    <div
      className={`min-h-full ${
        pathname.includes("events/new") ? "overflow-auto" : ""
      }`}
    >
      <Disclosure as="nav" className="bg-white border-b border-slate-200">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <img
                      className="h-10 w-auto md:h-10"
                      src={cciLogo}
                      alt="CCI GetDp"
                    />
                  </div>
                  <div className="hidden md:block">
                    <div className="ml-10 flex items-baseline space-x-4">
                      {navigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={classNames(
                            isActive(item.path)
                              ? "bg-artfulRed-dark text-white"
                              : "text-slate-600 hover:bg-slate-200 hover:text-slate-800",
                            "px-3 py-2 rounded-md text-sm font-medium"
                          )}
                          aria-current={
                            isActive(item.path) ? "page" : undefined
                          }
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="ml-4 flex items-center md:ml-6">
                    {/* <button
                      type="button"
                      className="rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                    >
                      <span className="sr-only">View notifications</span>
                      <BellIcon className="h-6 w-6" aria-hidden="true" />
                    </button> */}

                    <div>
                      <p className="font-bold tracking-wide text-gray-800">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-[12px] leading-none text-gray-400">
                        {user.role}
                      </p>
                    </div>

                    {/* Profile dropdown */}
                    <Menu as="div" className="relative ml-3">
                      <div>
                        <Menu.Button className="flex max-w-xs items-center rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                          <span className="sr-only">Open user menu</span>
                          <img
                            className="h-8 w-8 rounded-full"
                            src={user.image}
                            alt=""
                          />
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {userNavigation.map((item) => (
                            <Menu.Item key={item.name}>
                              {({ active }) => (
                                <Link
                                  to={item.path}
                                  className={classNames(
                                    active ? "bg-gray-100" : "",
                                    "block px-4 py-2 text-sm text-gray-700"
                                  )}
                                >
                                  {item.name}
                                </Link>
                              )}
                            </Menu.Item>
                          ))}
                          <Menu.Item>
                            <Link
                              onClick={logout}
                              className="block px-4 py-2 text-sm text-gray-700"
                            >
                              Log out
                            </Link>
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>
                </div>
                <div className="-mr-2 flex md:hidden">
                  {/* Mobile menu button */}
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-artfulRed">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="md:hidden">
              <div className="space-y-1 px-2 pt-2 pb-3 sm:px-3">
                {navigation.map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as={Link}
                    to={item.path}
                    className={classNames(
                      isActive(item.path)
                        ? "bg-artfulRed text-white"
                        : "text-slate-600 hover:bg-gray-100 hover:text-slate-500",
                      "block px-3 py-2 rounded-md text-base font-medium"
                    )}
                    aria-current={isActive(item.path) ? "page" : undefined}
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
              </div>
              <div className="border-t border-gray-200 pt-4 pb-3">
                <div className="flex items-center px-5">
                  <div className="flex-shrink-0">
                    <img
                      className="h-10 w-10 rounded-full"
                      src={user.image}
                      alt=""
                    />
                  </div>
                  <div className="ml-3">
                    <p className="text-base font-bold leading-none text-gray-700 tracking-wide">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-sm text-gray-400">
                      {user.email}
                    </p>
                    <p className="text-[12px] leading-none text-gray-400">
                      {user.role}
                    </p>
                  </div>
                  {/* <button
                    type="button"
                    className="ml-auto flex-shrink-0 rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                  >
                    <span className="sr-only">View notifications</span>
                    <BellIcon className="h-6 w-6" aria-hidden="true" />
                  </button> */}
                </div>
                <div className="mt-3 space-y-1 px-2">
                  {userNavigation.map((item) => (
                    <Disclosure.Button
                      key={item.name}
                      as={Link}
                      to={item.path}
                      className="block rounded-md px-3 py-2 text-base font-medium text-slate-600 hover:bg-gray-100 hover:text-slate-500"
                    >
                      {item.name}
                    </Disclosure.Button>
                  ))}
                  <Disclosure.Button
                    as={Link}
                    onClick={logout}
                    className="block rounded-md px-3 py-2 text-base font-medium text-slate-600 hover:bg-gray-100 hover:text-slate-500"
                  >
                    Log out
                  </Disclosure.Button>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      <Outlet />
    </div>
  );
};

export default Dashboard;
